import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalBody } from "reactstrap";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PDFIcon from "../../assets/image/icons/pdficon.png";
import DOCSIcon from "../../assets/image/icons/docs.svg";
import FitbitIcon from "@mui/icons-material/Fitbit";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  IconButton,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { ZoomIn, ZoomOut, Close as CloseIcon, FileDownloadOutlined } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import WhiteButton from "../Button/WhiteButton";
import { readFirstSheetAoA } from "../../utils/xlsxHelpers";
const DisplayImage = ({
  files,
  setFiles,
  IsDeleted,
  IsIndex = true,
  attachmentFileNamesEllipsis = false,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageZoom, setImageZoom] = useState(100);
  const [modalWidth, setModalWidth] = useState("sm");
  const [previewContent, setPreviewContent] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);
  const imgRef = useRef(null);
  const baseUrl = import.meta.env.VITE_BASE_API;

  const updateImageSize = () => {
    if (imgRef.current && selectedFile && selectedFileType === "image") {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const containerWidth = img.parentElement?.clientWidth || 560;
        const padding = 40;
        const availableWidth = containerWidth - padding;
        
        // Calculate image width based on zoom
        let imageWidth;
        if (imageZoom === 100) {
          imageWidth = Math.min(naturalWidth, availableWidth);
          img.style.width = "auto";
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.maxHeight = "none";
        } else {
          const displayWidthAt100 = Math.min(naturalWidth, availableWidth);
          imageWidth = (displayWidthAt100 * imageZoom) / 100;
          img.style.width = `${imageWidth}px`;
          img.style.height = "auto";
          img.style.maxWidth = "none";
          img.style.maxHeight = "none";
        }
        
        // Adjust modal width based on image size and zoom
        const totalWidth = imageWidth + padding + 80; // image + padding + dialog padding
        if (totalWidth < 500) {
          setModalWidth("xs");
        } else if (totalWidth < 700) {
          setModalWidth("sm");
        } else if (totalWidth < 900) {
          setModalWidth("md");
        } else {
          setModalWidth("lg");
        }
      }
    }
  };

  useEffect(() => {
    updateImageSize();
  }, [imageZoom, selectedFile, selectedFileType]);

  const renderFile = (file, index) => {
    const fileType =
      file?.type || (typeof file === "string" ? file?.split(".").pop()?.toLowerCase() : "");
    const isPdf = fileType === "pdf" || fileType?.includes("pdf") || 
                  (typeof file === "string" && file?.toLowerCase().endsWith(".pdf"));
    const isImage = fileType?.includes("image/") || 
                    (typeof file === "string" && file?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i));
    const isExcel = fileType === "application/vnd.ms-excel" ||
                    fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                    fileType === "application/vnd.ms-excel.sheet.macroEnabled.12" ||
                    (typeof file === "string" && file?.toLowerCase().match(/\.(xls|xlsx|excel)$/i)) ||
                    (file instanceof File && file.name?.toLowerCase().match(/\.(xls|xlsx|excel)$/i)) ||
                    (file instanceof Blob && file.name?.toLowerCase().match(/\.(xls|xlsx|excel)$/i));
    const isCSV = fileType === "text/csv" ||
                  (typeof file === "string" && file?.toLowerCase().endsWith(".csv"));
    const isTxt = fileType === "text/plain" ||
                  (typeof file === "string" && file?.toLowerCase().endsWith(".txt"));
    let fileURL = "";
    if (file instanceof File) {
      fileURL = URL.createObjectURL(file);
    } else if (typeof file === "string") {
      fileURL = `${baseUrl}/v1/cdn/upload/${file}`;
    }

    // CSV parsing function
    const parseCSV = (text) => {
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
      return lines.map((line) => {
        const result = [];
        let current = "";
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];
          
          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === "," && !inQuotes) {
            result.push(current.trim());
            current = "";
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      });
    };

    const handleFileClick = async () => {
      let resolvedFileURL = fileURL;
      // For remote PDFs, fetch and use a same-origin `blob:` URL — the
      // cross-origin file URL was loading as a sandboxed PDF that could
      // surface as a broken file icon under Chrome's Site Isolation. The
      // blob URL is revoked on dialog close.
      if (isPdf && typeof file === "string" && fileURL && /^https?:/i.test(fileURL)) {
        try {
          const response = await fetch(fileURL);
          if (response.ok) {
            const rawBlob = await response.blob();
            // Force application/pdf so the iframe renders the PDF viewer
            // instead of triggering a download.
            const pdfBlob =
              rawBlob.type === "application/pdf"
                ? rawBlob
                : new Blob([rawBlob], { type: "application/pdf" });
            resolvedFileURL = URL.createObjectURL(pdfBlob);
          }
        } catch (err) {
          console.error("PDF preview fetch failed:", err);
        }
      }
      setSelectedFile(resolvedFileURL);
      setPreviewPage(0);
      setPreviewRowsPerPage(10);
      setPreviewContent(null);
      setIsLoadingPreview(false);

      if (isPdf) {
        setSelectedFileType("pdf");
      } else if (isImage) {
        setSelectedFileType("image");
      } else if (isExcel) {
        setSelectedFileType("excel");
      } else if (isCSV) {
        setSelectedFileType("csv");
      } else if (isTxt) {
        setSelectedFileType("txt");
      } else {
        setSelectedFileType("other");
      }

      // Parse CSV or Excel files
      if (isCSV || isExcel) {
        setIsLoadingPreview(true);
        try {
          let blob = null;
          if (file instanceof File || file instanceof Blob) {
            blob = file;
          } else if (typeof file === "string") {
            const response = await fetch(fileURL);
            blob = await response.blob();
          }

          if (isCSV && blob) {
            const text = await blob.text();
            const rows = parseCSV(text);
            setPreviewContent({ type: "csv", data: rows });
          } else if (isExcel && blob) {
            try {
              const arrayBuffer = await blob.arrayBuffer();
              if (!arrayBuffer || arrayBuffer.byteLength === 0) {
                throw new Error("Excel file is empty or invalid");
              }
              const data = await readFirstSheetAoA(arrayBuffer);
              if (!data || data.length === 0) {
                throw new Error("Excel file is empty or has no sheets");
              }
              setPreviewContent({ type: "excel", data: data });
            } catch (excelError) {
              console.error("Error parsing Excel file:", excelError);
              // Don't show toast here, let it fail silently or show in console
            }
          }
        } catch (error) {
          console.error("Error parsing file:", error);
        } finally {
          setIsLoadingPreview(false);
        }
      }

      setModalOpen(true);
      setImageZoom(100); // Reset zoom when opening
      setModalWidth("sm"); // Reset to default
    };

    const fileName = file instanceof File ? file?.name : file;
    const shortenedFileName = fileName;

    return (
      <Grid
        className="d-flex gap-2 align-items-start display-file-main"
        key={fileName}
        style={{
          width: "100%",
          flexWrap: attachmentFileNamesEllipsis ? "nowrap" : "wrap",
        }}
      >
        <Grid
          className="d-flex gap-2 align-items-start image-main-section"
          style={{ flex: "1 1 220px", minWidth: 0, maxWidth: "100%" }}
        >
          {IsIndex && (
            <Grid
              style={{
                borderRadius: "50%",
                padding: "2px",
                marginRight: "10px",
                width: "25px",
                height: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                overflow: "hidden",
                flexShrink: 0,
              }}
              className="text-white-color bg-blue-color"
            >
              {index + 1}
            </Grid>
          )}
          {IsDeleted && (
            <DeleteForeverRoundedIcon
              style={{ color: "red", cursor: "pointer" }}
              onClick={() => handleDelete(file)}
            />
          )}
          <Box
            onClick={handleFileClick}
            style={{ cursor: "pointer", minWidth: 0, flex: "1 1 160px" }}
            className="text-blue-color"
            sx={{
              display: "flex",
              flexDirection: attachmentFileNamesEllipsis
                ? "row"
                : { xs: "column", sm: "row" },
              flexWrap: attachmentFileNamesEllipsis ? "nowrap" : "wrap",
              alignItems: attachmentFileNamesEllipsis
                ? "center"
                : { xs: "flex-start", sm: "center" },
              gap: { xs: "6px", sm: "8px" },
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              {fileType?.includes("image/") ||
              fileURL?.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                <img
                  src={fileURL}
                  height={"30px"}
                  style={{ paddingRight: "10px" }}
                  alt={shortenedFileName}
                />
              ) : fileType === "application/pdf" || fileURL?.match(/\.pdf$/i) ? (
                <img
                  src={PDFIcon}
                  alt="PDF Icon"
                  style={{
                    cursor: "pointer",
                    width: "30px",
                    height: "30px",
                    paddingRight: "10px",
                  }}
                />
              ) : fileType === "application/msword" ||
                fileType ===
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                fileURL?.match(/\.(doc|docx)$/i) ? (
                <img
                  src={DOCSIcon}
                  alt="DOCS Icon"
                  style={{
                    cursor: "pointer",
                    width: "30px",
                    height: "30px",
                    paddingRight: "10px",
                  }}
                />
              ) : fileType === "application/vnd.ms-excel" ||
                fileType ===
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                fileURL?.match(/\.(xls|xlsx|excel)$/i) ? (
                <DescriptionIcon
                  style={{
                    width: "30px",
                    height: "30px",
                    paddingRight: "10px",
                    color: "#217346",
                  }}
                />
              ) : fileType === "text/csv" || fileURL?.match(/\.csv$/i) ? (
                <DescriptionIcon
                  style={{
                    width: "30px",
                    height: "30px",
                    paddingRight: "10px",
                    color: "#0f2b46",
                  }}
                />
              ) : fileType === "text/plain" || fileURL?.match(/\.txt$/i) ? (
                <InsertDriveFileIcon
                  style={{
                    width: "30px",
                    height: "30px",
                    paddingRight: "10px",
                    color: "#0f2b46",
                  }}
                />
              ) : null}
            </Box>
            {attachmentFileNamesEllipsis ? (
              <Tooltip
                title={String(fileName || "").trim() || "Attachment"}
                placement="top"
                arrow
                enterDelay={250}
                componentsProps={{
                  tooltip: {
                    sx: { maxWidth: 420 },
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    flex: "1 1 0",
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    className="text-blue-color"
                    sx={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.45,
                    }}
                  >
                    {shortenedFileName}
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <Typography
                component="span"
                variant="body2"
                className="text-blue-color"
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  whiteSpace: "normal",
                  lineHeight: 1.45,
                  minWidth: 0,
                }}
              >
                {shortenedFileName}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const handleDelete = (fileToDelete) => {
    setFiles(files.filter((file) => file !== fileToDelete));
  };

  const closeFileModal = () => {
    if (selectedFile && typeof selectedFile === "string" && selectedFile.startsWith("blob:")) {
      URL.revokeObjectURL(selectedFile);
    }
    setModalOpen(false);
    setSelectedFile(null);
    setSelectedFileType(null);
    setPreviewContent(null);
    setImageZoom(100); // Reset zoom when closing
    setModalWidth("sm"); // Reset to default
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
  };

  const handlePreviewPageChange = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handlePreviewRowsPerPageChange = (event) => {
    setPreviewRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  const getPreviewData = () => {
    if (!previewContent?.data) return [];
    const dataWithoutHeader = previewContent.data.slice(1);
    const startIndex = previewPage * previewRowsPerPage;
    return dataWithoutHeader.slice(startIndex, startIndex + previewRowsPerPage);
  };

  const getTotalRows = () => {
    if (!previewContent?.data?.length) return 0;
    return Math.max(0, previewContent.data.length - 1);
  };

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 300)); // Max 300%
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 50)); // Min 50%
  };

  const handleResetZoom = () => {
    setImageZoom(100);
  };

  const handleDownload = async () => {
    if (selectedFile) {
      try {
        const response = await fetch(selectedFile);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = selectedFile.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        console.error("Error downloading file:", error);
        // Fallback: try direct download without opening in new tab
        const link = document.createElement("a");
        link.href = selectedFile;
        link.download = selectedFile.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    }
  };

  if (files?.length === 0) {
    return null;
  }

  return (
    <Grid
      className="d-flex flex-column gap-3 mt-2 border-blue-color"
      style={{
        border: "1px solid ",
        padding: "15px",
        borderRadius: "10px",
      }}
    >
      {files?.map((file, index) => renderFile(file, index))}
      {/* JOB-3411: the Notes / Contract / Quote / Invoice attachment
          preview rendered by DisplayImage capped PDF previews at the
          default modalWidth "sm" (500px Paper) with a 50vh iframe.
          When selectedFileType === "pdf" the Dialog now matches the
          unified Reports viewer (90% wide, max 1200px, 90vh tall, iframe
          fills the container). Image / csv / excel / txt branches keep
          their original modalWidth-derived sizing. */}
      {selectedFile && (
        <Dialog
          open={modalOpen}
          onClose={closeFileModal}
          maxWidth={selectedFileType === "pdf" ? "lg" : (previewContent?.type === "csv" || previewContent?.type === "excel" ? "lg" : modalWidth)}
          fullWidth={selectedFileType === "pdf" || previewContent?.type === "csv" || previewContent?.type === "excel"}
          PaperProps={{
            style: selectedFileType === "pdf"
              ? {
                  width: "90%",
                  maxWidth: "1200px",
                  height: "90vh",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-xl)",
                }
              : {
                  maxHeight: "90vh",
                  height: "auto",
                  width: "auto",
                  maxWidth: previewContent?.type === "csv" || previewContent?.type === "excel" ? "1200px" : (modalWidth === "xs" ? "400px" : modalWidth === "sm" ? "500px" : modalWidth === "md" ? "700px" : "900px"),
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-xl)",
                },
          }}
        >
          <DialogTitle
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#fff",
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Typography 
                className="text-blue-color borerBommoModel"
                style={{ fontWeight: 600, fontSize: "18px" }}
              >
                Preview
              </Typography>
              {(previewContent?.type === "csv" || previewContent?.type === "excel") && (
                <Chip
                  label={previewContent?.type === "csv" ? "CSV" : "EXCEL"}
                  style={{
                    backgroundColor: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "12px",
                    height: "24px",
                  }}
                />
              )}
            </Box>
            {selectedFile && selectedFileType === "image" && (
              <Box 
                style={{ 
                  display: "flex", 
                  gap: "4px", 
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                  padding: "4px 8px",
                  borderRadius: "8px",
                }}
              >
                <IconButton
                  onClick={handleZoomOut}
                  disabled={imageZoom <= 50}
                  size="small"
                  style={{ 
                    color: imageZoom <= 50 ? "#ccc" : "#0f2b46",
                    padding: "4px",
                  }}
                  title="Zoom Out"
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
                <Typography
                  variant="body2"
                  style={{
                    minWidth: "50px",
                    textAlign: "center",
                    color: "#0f2b46",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "0 8px",
                  }}
                >
                  {imageZoom}%
                </Typography>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={imageZoom >= 300}
                  size="small"
                  style={{ 
                    color: imageZoom >= 300 ? "#ccc" : "#0f2b46",
                    padding: "4px",
                  }}
                  title="Zoom In"
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
                {imageZoom !== 100 && (
                  <Button
                    onClick={handleResetZoom}
                    size="small"
                    variant="outlined"
                    style={{ 
                      color: "#0f2b46",
                      borderColor: "#0f2b46",
                      marginLeft: "8px",
                      padding: "2px 12px",
                      fontSize: "12px",
                      textTransform: "none",
                      minWidth: "auto",
                    }}
                    title="Reset Zoom"
                  >
                    Reset
                  </Button>
                )}
                <IconButton
                  onClick={handleDownload}
                  size="small"
                  style={{ 
                    color: "#0f2b46",
                    marginLeft: "8px",
                    padding: "4px",
                  }}
                  title="Download"
                >
                  <FileDownloadOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={closeFileModal}
                  size="small"
                  style={{ 
                    color: "#0f2b46", 
                    marginLeft: "8px",
                    padding: "4px",
                  }}
                  title="Close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {selectedFile && selectedFileType === "pdf" && (
              <Box style={{ display: "flex", gap: 1, alignItems: "center" }}>
                <IconButton
                  onClick={handleDownload}
                  size="small"
                  style={{ 
                    color: "#0f2b46",
                  }}
                  title="Download"
                >
                  <FileDownloadOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={closeFileModal}
                  size="small"
                  style={{ color: "#0f2b46" }}
                  title="Close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
            {selectedFile && selectedFileType !== "image" && selectedFileType !== "pdf" && (
              <Box style={{ display: "flex", gap: 1, alignItems: "center" }}>
                <IconButton
                  onClick={handleDownload}
                  size="small"
                  style={{ 
                    color: "#0f2b46",
                  }}
                  title="Download"
                >
                  <FileDownloadOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={closeFileModal}
                  size="small"
                  style={{ color: "#0f2b46" }}
                  title="Close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </DialogTitle>
          <DialogContent
            style={{
              textAlign: "center",
              padding: "0",
              overflow: "hidden",
              backgroundColor: "#f5f5f5",
              position: "relative",
              flex: "1 1 auto",
              minHeight: 0,
              maxHeight: "calc(90vh - 120px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isLoadingPreview ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                minHeight={400}
              >
                <CircularProgress />
              </Box>
            ) : (previewContent?.type === "csv" || previewContent?.type === "excel") ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "calc(90vh - 220px)",
                    overflow: "auto",
                    borderRadius: 0,
                    "& .MuiTable-root": {
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {previewContent?.data?.[0]?.map((header, index) => (
                          <TableCell
                            key={index}
                            className="bg-blue-color"
                            sx={{
                              textAlign: "left",
                              color: "white",
                              fontWeight: "bold",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              padding: { xs: "8px 12px", sm: "12px 16px" },
                              backgroundColor: "#2c3e50",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              "&:not(:last-child)": {
                                borderRight: "1px solid rgba(224, 224, 224, 0.4)",
                              },
                            }}
                            title={header}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getPreviewData().map((row, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          sx={{
                            "&:nth-of-type(even)": {
                              backgroundColor: "#f9f9f9",
                            },
                            "&:hover": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        >
                          {row.map((cell, cellIndex) => (
                            <TableCell
                              key={cellIndex}
                              sx={{
                                padding: { xs: "8px 12px", sm: "12px 16px" },
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                borderRight: "1px solid rgba(224, 224, 224, 0.2)",
                              }}
                            >
                              {cell || ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={getTotalRows()}
                  page={previewPage}
                  onPageChange={handlePreviewPageChange}
                  rowsPerPage={previewRowsPerPage}
                  onRowsPerPageChange={handlePreviewRowsPerPageChange}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    backgroundColor: "white",
                  }}
                />
              </Box>
            ) : selectedFileType === "pdf" ? (
              /* JOB-3411: fill the flex parent so the PDF expands to the
                 90vh unified viewer, not the prior fixed 50vh which made
                 the embed feel cramped inside a small modal. */
              <iframe
                src={selectedFile}
                style={{
                  width: "100%",
                  height: "100%",
                  flex: "1 1 auto",
                  minHeight: 0,
                  border: "none",
                  borderRadius: 0,
                  display: "block",
                }}
                title="File Preview"
              />
            ) : selectedFileType === "txt" ? (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "40vh",
                  padding: "40px",
                }}
              >
                <InsertDriveFileIcon
                  style={{
                    width: "80px",
                    height: "80px",
                    color: "#0f2b46",
                    marginBottom: "20px",
                  }}
                />
                <Typography variant="h6" style={{ marginBottom: "10px", fontWeight: 600 }}>
                  {selectedFile?.split("/").pop() || "Text File"}
                </Typography>
                <Typography variant="body2" style={{ color: "#666", textAlign: "center" }}>
                  Text files cannot be previewed in the browser. Please download to view.
                </Typography>
              </Box>
            ) : (
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "40vh",
                  padding: "20px",
                  width: "100%",
                  boxSizing: "border-box",
                  overflow: "auto",
                }}
              >
                <img
                  ref={imgRef}
                  src={selectedFile}
                  alt="Preview"
                  onLoad={updateImageSize}
                  style={{
                    width: "auto",
                    maxWidth: imageZoom === 100 ? "100%" : "none",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "var(--shadow-md)",
                    objectFit: "contain",
                    transition: "width 0.3s ease",
                    display: "block",
                  }}
                  draggable={false}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/images/no-image.png";
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions style={{ padding: "8px 24px" }}>
            <WhiteButton
              onClick={closeFileModal}
              label="Close"
            />
          </DialogActions>
        </Dialog>
      )}
    </Grid>
  );
};
const FileModal = ({ open, setOpen, file }) => {
  const baseUrl = import.meta.env.VITE_BASE_API;
  const fileURL =
    file instanceof File
      ? URL.createObjectURL(file)
      : `${baseUrl}/v1/cdn/upload/${file}`;
  const fileType =
    file?.type ||
    (typeof file === "string" ? `application/${file?.split(".").pop()}` : "");

  const fileName = file instanceof File ? file?.name : file;

  // Same-origin `blob:` URL for previewing remote PDFs in the iframe — the
  // cross-origin file URL was loading as a sandboxed PDF that could surface
  // as a broken file icon under Chrome's Site Isolation. The blob URL is
  // revoked when the modal closes or the file changes.
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const isPdf = fileType === "application/pdf" || fileURL?.match(/\.pdf$/i);
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    if (open && isPdf && typeof file === "string" && fileURL) {
      (async () => {
        try {
          const response = await fetch(fileURL);
          if (!response.ok || cancelled) return;
          const rawBlob = await response.blob();
          // Force application/pdf so the iframe renders the PDF viewer
          // instead of triggering a download.
          const blob =
            rawBlob.type === "application/pdf"
              ? rawBlob
              : new Blob([rawBlob], { type: "application/pdf" });
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) {
            setPdfBlobUrl(createdUrl);
          } else {
            URL.revokeObjectURL(createdUrl);
          }
        } catch (err) {
          console.error("PDF preview fetch failed:", err);
        }
      })();
    }
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
      setPdfBlobUrl(null);
    };
  }, [open, fileURL, isPdf, file]);

  const renderContent = () => {
    if (!fileURL) return <p>No file to display.</p>;

    if (
      fileType?.includes("image/") ||
      fileURL?.match(/\.(jpg|jpeg|png|webp)$/i)
    ) {
      return <img src={fileURL} width={"100%"} alt="Image" />;
    } else if (isPdf) {
      return (
        <iframe
          src={pdfBlobUrl || fileURL}
          style={{ width: "100%", height: "80vh" }}
          className="p-0 m-0"
          title="PDF Viewer"
        />
      );
    } else if (
      fileType === "application/msword" ||
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      fileURL?.match(/\.(doc|docx)$/i)
    ) {
      const encodedURL = encodeURIComponent(fileURL);
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodedURL}`}
          style={{ width: "100%", height: "100vh" }}
          title="Document Viewer"
        />
      );
    } else if (
      fileType === "application/vnd.ms-excel" ||
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileURL?.match(/\.(xls|xlsx|excel)$/i)
    ) {
      return (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
            padding: "40px",
          }}
        >
          <DescriptionIcon
            style={{
              width: "80px",
              height: "80px",
              color: "#217346",
              marginBottom: "20px",
            }}
          />
          <Typography variant="h6" style={{ marginBottom: "10px", fontWeight: 600 }}>
            {fileName || "Excel File"}
          </Typography>
          <Typography variant="body2" style={{ color: "#666", textAlign: "center" }}>
            Excel files cannot be previewed in the browser. Please download to view.
          </Typography>
        </Box>
      );
    } else if (fileType === "text/plain" || fileURL?.match(/\.txt$/i)) {
      return (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
            padding: "40px",
          }}
        >
          <InsertDriveFileIcon
            style={{
              width: "80px",
              height: "80px",
              color: "#0f2b46",
              marginBottom: "20px",
            }}
          />
          <Typography variant="h6" style={{ marginBottom: "10px", fontWeight: 600 }}>
            {fileName || "Text File"}
          </Typography>
          <Typography variant="body2" style={{ color: "#666", textAlign: "center" }}>
            Text files cannot be previewed in the browser. Please download to view.
          </Typography>
        </Box>
      );
    } else {
      return (
        <Grid>
          <p>Unable to display this file type.</p>
          <a href={fileURL} target="_blank" rel="noopener noreferrer">
            Open file
          </a>
        </Grid>
      );
    }
  };

  return (
    <Modal isOpen={open} toggle={() => setOpen(!open)}>
      <ModalBody className="p-0 m-0">{renderContent()}</ModalBody>
    </Modal>
  );
};

export { DisplayImage, FileModal };
